/**
 * Global Property Utility Functions
 * 
 * Pure utility functions for global property data transformation and manipulation.
 * These functions are framework-agnostic and can be used in any context.
 */

import {
  StateActions,
  StateAction,
  StateActionEntry,
  TransitionChange,
  TransitionChangeReference,
  ChangeTypeDefinition,
  CalculationDependencies,
  ValidationRules,
  getChangeTypeName,
  normalizeStateAction,
  isReferenceBasedTransitionChange
} from '../types/globalProperties';
import { getForwardTransitions, getBackwardTransitions } from '../types/standardStates';

/**
 * Check if a property type is numeric
 */
export const isNumericPropertyType = (type: string): boolean => {
  return ['number', 'integer', 'float', 'currency', 'duration'].includes(type);
};

/**
 * Get operator options based on calculation type
 */
export const getOperatorOptions = (calcType: string | undefined): Array<{ value: string; label: string }> | null => {
  if (!calcType) return [];
  
  switch(calcType) {
    case 'divide':
      return [
        { value: 'numerator', label: 'Numerator (Dividend)' },
        { value: 'denominator', label: 'Denominator (Divisor)' }
      ];
    case 'subtract':
      return [
        { value: 'operand1', label: 'Operand 1 (Subtract From)' },
        { value: 'operand2', label: 'Operand 2 (Subtract)' }
      ];
    case 'multiply':
    case 'add':
      return [
        { value: 'operand1', label: 'Operand 1' },
        { value: 'operand2', label: 'Operand 2' },
        { value: 'operand3', label: 'Operand 3' },
        { value: 'operand4', label: 'Operand 4' },
        { value: 'operand5', label: 'Operand 5' }
      ];
    case 'sum':
    case 'average':
      return [
        { value: 'value1', label: 'Value 1' },
        { value: 'value2', label: 'Value 2' },
        { value: 'value3', label: 'Value 3' },
        { value: 'value4', label: 'Value 4' },
        { value: 'value5', label: 'Value 5' }
      ];
    case 'custom':
      return null; // Free-form text input
    default:
      return [];
  }
};

/**
 * Convert state_actions to array format (handles backward compatibility with nested format)
 * Standard format is now array: StateActionEntry[]
 */
export const convertStateActionsToArray = (stateActions: StateActions | StateActionEntry[] | undefined, localChangeTypeDefinitions?: TransitionChangeReference[]): StateActionEntry[] => {
  if (!stateActions) return [];
  
  // If already an array, return as-is
  if (Array.isArray(stateActions)) {
    return stateActions;
  }
  
  // Backward compatibility: Convert nested format to array (legacy support during migration)
  const entries: StateActionEntry[] = [];
  
  if (stateActions.operational) {
    Object.entries(stateActions.operational).forEach(([state, action]) => {
      if (action && action !== 'none') {
        const normalizedAction = normalizeStateAction(action) as StateAction;
        // Try to find matching change_type_name from localChangeTypeDefinitions
        const changeTypeName = localChangeTypeDefinitions?.find(ct => ct.state_action_type === normalizedAction)?.change_type_name || normalizedAction;
        entries.push({
          state,
          flow_type: 'operational',
          state_action_type: normalizedAction,
          flow_direction: 'both', // Default for legacy format
          change_type: changeTypeName // Use matching change_type_name if available
        });
      }
    });
  }
  
  if (stateActions.improvement) {
    Object.entries(stateActions.improvement).forEach(([state, action]) => {
      if (action && action !== 'none') {
        const normalizedAction = normalizeStateAction(action) as StateAction;
        // Try to find matching change_type_name from localChangeTypeDefinitions
        const changeTypeName = localChangeTypeDefinitions?.find(ct => ct.state_action_type === normalizedAction)?.change_type_name || normalizedAction;
        entries.push({
          state,
          flow_type: 'improvement',
          state_action_type: normalizedAction,
          flow_direction: 'both', // Default for legacy format
          change_type: changeTypeName // Use matching change_type_name if available
        });
      }
    });
  }
  
  return entries;
};

/**
 * @deprecated No longer needed - state_actions now uses array format directly
 * This function is kept temporarily for migration purposes only
 */
export const convertStateActionsToNested = (entries: StateActionEntry[]): StateActions => {
  const result: StateActions = {
    operational: {},
    improvement: {}
  };
  
  entries.forEach(entry => {
    if (entry.flow_type === 'operational') {
      result.operational![entry.state] = entry.state_action_type;
    } else if (entry.flow_type === 'improvement') {
      result.improvement![entry.state] = entry.state_action_type;
    }
  });
  
  return result;
};

/**
 * Parse reference-based transition changes back into ChangeTypeDefinition array
 */
export const parseReferenceBasedTransitionChanges = (
  transitionChanges: (TransitionChange | TransitionChangeReference)[],
  calculationDependencies?: CalculationDependencies | Record<string, any>,
  validationRules?: ValidationRules | Record<string, any>
): ChangeTypeDefinition[] => {
  if (!transitionChanges || transitionChanges.length === 0) {
    return [];
  }

  // Detect format: check if first entry is reference-based (has change_type_name) or inline (has from_state)
  const firstEntry = transitionChanges[0];
  const isReferenceBased = isReferenceBasedTransitionChange(firstEntry);
  
  if (!isReferenceBased) {
    // Old inline format - return empty array, let existing logic handle it
    console.log('[globalPropertyUtils] parseReferenceBasedTransitionChanges: Detected inline format (old), skipping');
    return [];
  }

  // New reference-based format - resolve references and rebuild change types
  const changeTypes: ChangeTypeDefinition[] = [];
  const calcDeps = calculationDependencies as Record<string, any> || {};
  const valRules = validationRules as Record<string, any> || {};

  transitionChanges.forEach((tc) => {
    if (!isReferenceBasedTransitionChange(tc)) return; // Skip non-reference entries
    const changeType: ChangeTypeDefinition = {
      change_type_name: tc.change_type_name,
      state_action_type: tc.state_action_type as StateAction
    };

    // Resolve user_input reference
    if (tc.user_input && calcDeps[tc.user_input]) {
      changeType.user_input = calcDeps[tc.user_input];
    }

    // Resolve calculation reference
    if (tc.calculation && calcDeps[tc.calculation]) {
      const calcConfig = calcDeps[tc.calculation];
      // Check if it's a System change type (function calculation) or Calculate
      if (calcConfig.calculation_type === 'function') {
        // System change type - convert function calculation to system config
        changeType.system = {
          system_type: calcConfig.function_name,
          ...(calcConfig.function_parameters?.default_value !== undefined && { default_value: calcConfig.function_parameters.default_value }),
          ...(calcConfig.function_parameters?.reference_type && { reference_type: calcConfig.function_parameters.reference_type })
        };
      } else {
        // Calculate change type
        changeType.calculate = {
          calculation_type: calcConfig.calculation_type,
          ...(calcConfig.transformation_logic && { transformation_logic: calcConfig.transformation_logic }),
          ...(calcConfig.source_properties && { source_properties: calcConfig.source_properties }),
          ...(calcConfig.calculation_config && { calculation_config: calcConfig.calculation_config })
        };
      }
    }

    // Resolve reference reference
    if (tc.reference && calcDeps[tc.reference]) {
      changeType.reference = calcDeps[tc.reference];
    }

    // Resolve condition reference
    if (tc.condition && valRules[tc.condition]) {
      changeType.conditions = valRules[tc.condition];
    }

    // Resolve validation reference
    if (tc.validation && valRules[tc.validation]) {
      changeType.validation = valRules[tc.validation];
    }

    // Resolve resource reference
    if (tc.resource && valRules[tc.resource]) {
      const resourceConfig = valRules[tc.resource];
      // Convert resource_type from lowercase to uppercase format
      let resourceType: 'PROCESS_RESOURCE' | 'USER' | 'SYSTEM' | undefined = undefined;
      if (resourceConfig.resource_type) {
        const rt = resourceConfig.resource_type.toLowerCase();
        if (rt === 'process') resourceType = 'PROCESS_RESOURCE';
        else if (rt === 'system') resourceType = 'SYSTEM';
        else if (rt === 'user') resourceType = 'USER';
      }
      changeType.resource = {
        ...(resourceType && { resource_type: resourceType }),
        ...(resourceConfig.resource_skill && { required_skills: [resourceConfig.resource_skill] }),
        ...(resourceConfig.description && { description: resourceConfig.description })
      };
    }

    changeTypes.push(changeType);
  });

  return changeTypes;
};

/**
 * Interface for extracted configurations
 */
export interface ExtractedConfigurations {
  calculationDependencies: Record<string, any>;
  validationRules: Record<string, any>;
  referenceMapping: Record<string, {
    user_input?: string;
    calculation?: string;
    reference?: string;
    condition?: string;
    validation?: string;
    resource?: string;
  }>;
}

/**
 * Extract named configurations from change types for reference-based structure
 */
export const extractNamedConfigurations = (changeTypes: ChangeTypeDefinition[]): ExtractedConfigurations => {
  const calculationDependencies: Record<string, any> = {};
  const validationRules: Record<string, any> = {};
  const referenceMapping: Record<string, {
    user_input?: string;
    calculation?: string;
    reference?: string;
    condition?: string;
    validation?: string;
    resource?: string;
  }> = {};

  // Counters for naming (e.g., user_input1, user_input2, calculation1, condition1)
  let userInputCounter = 0;
  let calculationCounter = 0;
  let referenceCounter = 0;
  let conditionCounter = 0;
  let validationCounter = 0;
  let resourceCounter = 0;

  changeTypes.forEach(changeType => {
    const changeTypeName = getChangeTypeName(changeType);
    const mapping: {
      user_input?: string;
      calculation?: string;
      reference?: string;
      condition?: string;
      validation?: string;
      resource?: string;
    } = {};

    // Extract type-specific configurations to calculation_dependencies
    if (changeType.user_input) {
      userInputCounter++;
      const refName = `user_input${userInputCounter}`;
      // Named configs can have additional properties beyond the type definition
      const userInputConfig = changeType.user_input as any;
      calculationDependencies[refName] = {
        input_fields: userInputConfig.input_fields || [],
        ...(userInputConfig.input_type && { input_type: userInputConfig.input_type }),
        ...(userInputConfig.label && { label: userInputConfig.label }),
        ...(userInputConfig.default_value !== undefined && { default_value: userInputConfig.default_value }),
        ...(userInputConfig.help_text && { help_text: userInputConfig.help_text }),
        ...(userInputConfig.source && { source: userInputConfig.source })
      };
      mapping.user_input = refName;
    }

    if (changeType.calculate) {
      calculationCounter++;
      const refName = `calculation${calculationCounter}`;
      calculationDependencies[refName] = {
        ...(changeType.calculate.calculation_type && { calculation_type: changeType.calculate.calculation_type }),
        ...(changeType.calculate.transformation_logic && { transformation_logic: changeType.calculate.transformation_logic }),
        ...(changeType.calculate.source_properties && { source_properties: changeType.calculate.source_properties }),
        ...(changeType.calculate.calculation_config && { calculation_config: changeType.calculate.calculation_config })
      };
      mapping.calculation = refName;
    }

    if (changeType.system) {
      // System change types use calculation references with calculation_type: "function"
      calculationCounter++;
      const refName = `calculation${calculationCounter}`;
      const functionParams: any = {};
      if (changeType.system.default_value !== undefined) functionParams.default_value = changeType.system.default_value;
      if (changeType.system.reference_type) functionParams.reference_type = changeType.system.reference_type;
      if (changeType.system.system_type) functionParams.system_type = changeType.system.system_type;
      
      calculationDependencies[refName] = {
        calculation_type: 'function',
        ...(changeType.system.system_type && { function_name: changeType.system.system_type }),
        ...(Object.keys(functionParams).length > 0 && { function_parameters: functionParams }),
        source_properties: []
      };
      mapping.calculation = refName;
    }

    if (changeType.reference) {
      referenceCounter++;
      const refName = `reference${referenceCounter}`;
      calculationDependencies[refName] = {
        ...(changeType.reference.artifact_type && { artifact_type: changeType.reference.artifact_type }),
        ...(changeType.reference.artifact_item && { artifact_item: changeType.reference.artifact_item }),
        ...(changeType.reference.property_key && { property_key: changeType.reference.property_key }),
        ...(changeType.reference.filter && { filter: changeType.reference.filter }),
        ...(changeType.reference.selection_method && { selection_method: changeType.reference.selection_method }),
        ...(changeType.reference.source && { source: changeType.reference.source }),
        reference_type: changeType.reference.reference_type || 'simple'
      };
      mapping.reference = refName;
    }

    // Extract conditions/validation/resource to validation_rules
    if (changeType.conditions && (changeType.conditions.when || changeType.conditions.required_if)) {
      conditionCounter++;
      const refName = `condition${conditionCounter}`;
      validationRules[refName] = {
        ...(changeType.conditions.when && { when: changeType.conditions.when }),
        ...(changeType.conditions.required_if && { required_if: changeType.conditions.required_if })
      };
      mapping.condition = refName;
    }

    if (changeType.validation) {
      validationCounter++;
      const refName = `validation${validationCounter}`;
      validationRules[refName] = {
        ...(changeType.validation.required !== undefined && { required: changeType.validation.required }),
        ...(changeType.validation.min !== undefined && { min: changeType.validation.min }),
        ...(changeType.validation.max !== undefined && { max: changeType.validation.max }),
        ...(changeType.validation.min_length !== undefined && { min_length: changeType.validation.min_length }),
        ...(changeType.validation.max_length !== undefined && { max_length: changeType.validation.max_length }),
        ...(changeType.validation.pattern && { pattern: changeType.validation.pattern }),
        ...(changeType.validation.type_specific_validation && { type_specific_validation: changeType.validation.type_specific_validation })
      };
      mapping.validation = refName;
    }

    if (changeType.resource) {
      resourceCounter++;
      const refName = `resource${resourceCounter}`;
      // Convert resource_type from uppercase to lowercase for validation_rules
      let resourceType: 'process' | 'user' | 'system' | undefined = undefined;
      if (changeType.resource.resource_type) {
        const rt = changeType.resource.resource_type;
        const rtStr = String(rt); // Convert to string for safe comparison
        if (rt === 'PROCESS_RESOURCE' || rtStr.toLowerCase().includes('process')) {
          resourceType = 'process';
        } else if (rt === 'SYSTEM' || rtStr.toLowerCase().includes('system')) {
          resourceType = 'system';
        } else if (rt === 'USER' || rtStr.toLowerCase().includes('user')) {
          resourceType = 'user';
        }
      }
      validationRules[refName] = {
        ...(resourceType && { resource_type: resourceType }),
        ...(changeType.resource.required_skills && changeType.resource.required_skills.length > 0 && { resource_skill: changeType.resource.required_skills[0] }),
        ...(changeType.resource.description && { description: changeType.resource.description })
      };
      mapping.resource = refName;
    }

    if (Object.keys(mapping).length > 0) {
      referenceMapping[changeTypeName] = mapping;
    }
  });

  return {
    calculationDependencies,
    validationRules,
    referenceMapping
  };
};

/**
 * Extract available named configurations from calculation_dependencies and validation_rules
 * Returns arrays of configuration names (e.g., ["user_input1", "user_input2"], ["calculation1"], etc.)
 */
export function getAvailableNamedConfigurations(
  calculationDependencies?: CalculationDependencies | Record<string, any>,
  validationRules?: ValidationRules | Record<string, any>
): {
  user_inputs: string[];
  calculations: string[];
  references: string[];
  conditions: string[];
  validations: string[];
  resources: string[];
} {
  const user_inputs: string[] = [];
  const calculations: string[] = [];
  const references: string[] = [];
  const conditions: string[] = [];
  const validations: string[] = [];
  const resources: string[] = [];

  // Extract from calculation_dependencies
  if (calculationDependencies && typeof calculationDependencies === 'object') {
    Object.keys(calculationDependencies).forEach(key => {
      // Skip non-config keys like 'type', 'source_properties', etc.
      if (['type', 'source_properties', 'calculation_config', 'user_input_label', 'user_input_help_text', 'user_input_default_value', 'user_input', 'reference', 'reference_id', 'referenced_properties'].includes(key)) {
        return;
      }
      
      if (key.match(/^user_input\d+$/)) {
        user_inputs.push(key);
      } else if (key.match(/^calculation\d+$/)) {
        calculations.push(key);
      } else if (key.match(/^reference\d+$/)) {
        references.push(key);
      }
    });
  }

  // Extract from validation_rules
  if (validationRules && typeof validationRules === 'object') {
    Object.keys(validationRules).forEach(key => {
      // Skip non-config keys like 'required', 'min', 'max', etc.
      if (['required', 'min', 'max', 'min_length', 'max_length', 'pattern', 'divide_by_zero_check', 'date_range_validation', 'currency_precision', 'enum', 'type', 'limit_to_list', 'update_scope'].includes(key)) {
        return;
      }
      
      if (key.match(/^condition\d+$/)) {
        conditions.push(key);
      } else if (key.match(/^validation\d+$/)) {
        validations.push(key);
      } else if (key.match(/^resource\d+$/)) {
        resources.push(key);
      }
    });
  }

  // Sort arrays numerically (e.g., user_input1, user_input2, user_input10)
  const numericSort = (a: string, b: string) => {
    const numA = parseInt(a.match(/\d+$/)?.[0] || '0', 10);
    const numB = parseInt(b.match(/\d+$/)?.[0] || '0', 10);
    return numA - numB;
  };

  return {
    user_inputs: user_inputs.sort(numericSort),
    calculations: calculations.sort(numericSort),
    references: references.sort(numericSort),
    conditions: conditions.sort(numericSort),
    validations: validations.sort(numericSort),
    resources: resources.sort(numericSort)
  };
}

/**
 * Build reference-based transition changes from change types and reference mapping
 */
export const buildReferenceBasedTransitionChanges = (
  changeTypes: ChangeTypeDefinition[],
  referenceMapping: Record<string, {
    user_input?: string;
    calculation?: string;
    reference?: string;
    condition?: string;
    validation?: string;
    resource?: string;
  }>
): TransitionChangeReference[] => {
  const transitionChanges: TransitionChangeReference[] = [];

  changeTypes.forEach(changeType => {
    const changeTypeName = getChangeTypeName(changeType);
    
    // Infer state_action_type from change type
    let stateActionType: 'System' | 'User' | 'Calculate' | 'Reference';
    if (changeType.user_input) stateActionType = 'User';
    else if (changeType.calculate) stateActionType = 'Calculate';
    else if (changeType.system) stateActionType = 'System';
    else if (changeType.reference) stateActionType = 'Reference';
    else {
      // Use state_action_type from change type if available, otherwise skip
      if (changeType.state_action_type && changeType.state_action_type !== 'none') {
        stateActionType = changeType.state_action_type as 'System' | 'User' | 'Calculate' | 'Reference';
      } else {
        console.warn(`[globalPropertyUtils] Cannot determine state_action_type for change type: ${changeTypeName}`);
        return;
      }
    }

    const mapping = referenceMapping[changeTypeName] || {};
    
    const transitionChange: TransitionChangeReference = {
      change_type_name: changeTypeName,
      state_action_type: stateActionType,
      ...(mapping.user_input && { user_input: mapping.user_input }),
      ...(mapping.calculation && { calculation: mapping.calculation }),
      ...(mapping.reference && { reference: mapping.reference }),
      ...(mapping.condition && { condition: mapping.condition }),
      ...(mapping.validation && { validation: mapping.validation }),
      ...(mapping.resource && { resource: mapping.resource })
    };

    transitionChanges.push(transitionChange);
  });

  return transitionChanges;
};

/**
 * Convert ChangeTypeDefinition to TransitionChange entries (LEGACY - for backward compatibility reading only)
 * Note: stateActions can be in nested format (for backward compatibility) or array format (new contract format)
 */
export const convertChangeTypesToTransitionChanges = (
  changeTypes: ChangeTypeDefinition[],
  stateActions: StateActions | StateActionEntry[] | undefined
): TransitionChange[] => {
  const transitionChanges: TransitionChange[] = [];
  
  if (!changeTypes || changeTypes.length === 0) {
    console.log('[globalPropertyUtils] convertChangeTypesToTransitionChanges: No change types to convert');
    return transitionChanges;
  }
  
  // Convert stateActions to array format for processing (handles both nested and array formats)
  const stateActionsArray = convertStateActionsToArray(stateActions);
  if (!stateActionsArray || stateActionsArray.length === 0) {
    console.log('[globalPropertyUtils] convertChangeTypesToTransitionChanges: No state actions available');
    return transitionChanges;
  }
  
  console.log('[globalPropertyUtils] convertChangeTypesToTransitionChanges: Starting conversion', {
    changeTypesCount: changeTypes.length,
    stateActionsCount: stateActionsArray.length
  });
  
  // Get all transitions
  const operationalForward = getForwardTransitions('operational');
  const operationalBackward = getBackwardTransitions('operational');
  const improvementForward = getForwardTransitions('improvement');
  const improvementBackward = getBackwardTransitions('improvement');
  
  // Process each change type
  changeTypes.forEach(changeType => {
    const changeTypeName = getChangeTypeName(changeType);
    const flowDirection = changeType.flow_direction || 'both';
    
    // Infer which state action type this change type belongs to
    let actionType: StateAction | null = null;
    if (changeType.user_input) actionType = 'User';
    else if (changeType.calculate) actionType = 'Calculate';
    else if (changeType.system) actionType = 'System';
    else if (changeType.reference) actionType = 'Reference';
    
    console.log('[globalPropertyUtils] Processing change type:', {
      changeTypeName,
      flowDirection,
      actionType,
      changeType
    });
    
    // Find matching states in state_actions (now using array format)
    ['operational', 'improvement'].forEach(flowType => {
      // Filter state actions for this flow type
      const flowActions = stateActionsArray.filter(
        entry => entry.flow_type === flowType && entry.state_action_type !== 'none'
      );
      
      if (!flowActions || flowActions.length === 0) {
        console.log(`[globalPropertyUtils] No ${flowType} state actions`);
        return;
      }
      
      // Create a map for easier lookup
      const flowActionsMap: Record<string, string> = {};
      flowActions.forEach(entry => {
        flowActionsMap[entry.state] = entry.state_action_type;
      });
      
      console.log(`[globalPropertyUtils] Checking ${flowType} flow actions:`, flowActionsMap);
      
        // Process each state action entry for this flow type
        flowActions.forEach(stateActionEntry => {
          const { state, state_action_type, flow_direction: entryFlowDirection, change_type: entryChangeType } = stateActionEntry;
          const normalizedAction = normalizeStateAction(state_action_type);
          console.log(`[globalPropertyUtils] Checking state ${state} with action ${normalizedAction} against change type action ${actionType}`);
          
          // Check if this state action uses this change type
          if (entryChangeType !== changeTypeName) {
            console.log(`[globalPropertyUtils] Change type mismatch: ${entryChangeType} !== ${changeTypeName}, skipping`);
            return;
          }
          
          // More flexible matching: System can use Calculate change types (system performs calculations)
          // Calculate change types can be used for System state actions
          if (actionType && normalizedAction !== actionType) {
            // Allow System state actions to use Calculate change types
            if (normalizedAction === 'System' && actionType === 'Calculate') {
              console.log(`[globalPropertyUtils] Allowing System state action to use Calculate change type`);
            } else {
              console.log(`[globalPropertyUtils] Action mismatch: ${normalizedAction} !== ${actionType}, skipping`);
              return;
            }
          }
          
          // Use flow_direction from state action entry if available, otherwise use change type flow_direction
          const effectiveFlowDirection = entryFlowDirection || flowDirection;
          
          // Get transitions for this state
          const transitions = flowType === 'operational'
            ? (effectiveFlowDirection === 'forward' ? operationalForward : 
               effectiveFlowDirection === 'backward' ? operationalBackward :
               [...operationalForward, ...operationalBackward])
            : (effectiveFlowDirection === 'forward' ? improvementForward :
               effectiveFlowDirection === 'backward' ? improvementBackward :
               [...improvementForward, ...improvementBackward]);
        
        // Create TransitionChange entries for transitions TO this state
        const matchingTransitions = transitions.filter(trans => trans.toState === state);
        console.log(`[globalPropertyUtils] Found ${matchingTransitions.length} transitions to state ${state}`);
        
        matchingTransitions.forEach(trans => {
          const transitionStr = `${trans.fromState} → ${trans.toState}`;
          const stateActionStr = `${flowType}.${state}`;
          
          // Create a unique key for this transition to prevent duplicates
          const transitionKey = `${trans.fromState}_${trans.toState}_${flowType}`;
          
          // Skip if we've already created a transition change for this exact transition
          // (multiple change types might match the same state/action, but we only want one transition change per transition)
          // Check both from_state/to_state format and legacy transition string format
          const isDuplicate = transitionChanges.some(tc => {
            // Check new format (from_state/to_state)
            if (tc.from_state && tc.to_state) {
              return tc.from_state === trans.fromState && 
                     tc.to_state === trans.toState && 
                     (tc.flow_direction === flowDirection || !tc.flow_direction);
            }
            // Check legacy format (transition string)
            if (tc.transition) {
              const legacyTransition = `${trans.fromState} → ${trans.toState}`;
              return tc.transition === legacyTransition;
            }
            return false;
          });
          
          if (isDuplicate) {
            console.log(`[globalPropertyUtils] Skipping duplicate transition: ${transitionKey} (${trans.fromState} → ${trans.toState})`);
            return;
          }
          
          // Convert change type to inline TransitionChange configuration
          // Backend expects: from_state, to_state, change_type (capitalized), flow_direction, state_action_type
          // NOTE: state_action (without "_type") is metadata used for matching, NOT part of the stored data structure
          // state_action_type (with "_type") IS a valid field that should be stored
          const transitionChange: TransitionChange = {
            from_state: trans.fromState,
            to_state: trans.toState,
            // ❌ DO NOT include state_action - it's metadata only, not part of transition_changes structure
            // state_action: stateActionStr, // REMOVED per backend diagnosis
            // ✅ Include state_action_type - this is a valid field that should be stored
            state_action_type: changeType.state_action_type || actionType as StateAction,
            flow_direction: effectiveFlowDirection || 'both',
            // Map change type to inline fields - use capitalized change_type names
            // CRITICAL: When change_type is 'User', backend REQUIRES a user_input object to be present
            ...(changeType.user_input && changeType.user_input.input_fields && Array.isArray(changeType.user_input.input_fields) && changeType.user_input.input_fields.length > 0 && { 
              change_type: 'User', // Capitalized
              user_input: {
                input_fields: changeType.user_input.input_fields
                // Other user_input properties (input_type, label, default_value, required) come from property-level config, not from change type
                // Backend may expect these fields but they're optional - backend should use property-level defaults
              }
            }),
            // If actionType is 'User' but we don't have user_input in the change type definition,
            // we still need to include a user_input object because change_type 'User' requires it
            // This ensures backward compatibility with legacy data that might have User state actions but no user_input config
            ...(actionType === 'User' && !changeType.user_input && { 
              change_type: 'User',
              user_input: {
                input_fields: [] // Empty array is valid - backend will use defaults
              }
            }),
            ...(changeType.calculate && { 
              change_type: 'Calculate', // Capitalized
              calculations: {
                calculation_type: changeType.calculate.calculation_type,
                transformation_logic: changeType.calculate.transformation_logic,
                ...(changeType.calculate.source_properties && { source_properties: changeType.calculate.source_properties })
              }
            }),
            ...(changeType.system && { 
              change_type: 'System', // Capitalized
              system: {
                default_value: changeType.system.default_value,
                ...(changeType.system.system_type && { system_type: changeType.system.system_type }),
                ...(changeType.system.reference_type && { reference_type: changeType.system.reference_type })
              }
            }),
            // For Reference types: use change_type: 'Reference' and store reference details in reference object
            // Valid change types are: "System", "User", "Calculate", "Reference"
            ...(changeType.reference && {
              change_type: 'Reference', // Capitalized
              reference: {
                ...(changeType.reference.artifact_type && { artifact_type: changeType.reference.artifact_type }),
                ...(changeType.reference.artifact_item && { artifact_item: changeType.reference.artifact_item }),
                ...(changeType.reference.property_key && { property_key: changeType.reference.property_key }),
                ...(changeType.reference.filter && { filter: changeType.reference.filter }),
                ...(changeType.reference.selection_method && { selection_method: changeType.reference.selection_method }),
                ...(changeType.reference.source && { source: changeType.reference.source }),
                reference_type: changeType.reference.reference_type || 'simple'
              }
            }),
            // Add required fields: conditions, validation (even if empty)
            // Backend expects these fields to be present (per GLOBAL_PROPERTY_STRUCTURE.md and ISSUE-116.md)
            // CRITICAL: Backend requires these fields to always be present (per ISSUE-116.md)
            // conditions must be an object (can be empty {})
            conditions: (changeType.conditions && typeof changeType.conditions === 'object' && !Array.isArray(changeType.conditions))
              ? changeType.conditions 
              : {},
            // validation must have at least { required: false } per examples in GLOBAL_PROPERTY_STRUCTURE.md
            validation: (changeType.validation && typeof changeType.validation === 'object' && !Array.isArray(changeType.validation) && Object.keys(changeType.validation).length > 0)
              ? changeType.validation 
              : { required: false },
            // Resource field - must always be present (even if empty object)
            // Backend expects this field to be present (per ISSUE-116.md)
            // Always include resource object, even if empty
            resource: (changeType.resource && changeType.resource.required_skills && Array.isArray(changeType.resource.required_skills) && changeType.resource.required_skills.length > 0) ? {
              // Normalize resource_type to lowercase (backend expects "user", "system", "process")
              resource_type: ((): 'user' | 'system' | 'process' => {
                const rt: string = changeType.resource!.resource_type || 'USER';
                // Map explicitly: "PROCESS_RESOURCE" -> "process", "USER" -> "user", "SYSTEM" -> "system"
                if (rt === 'PROCESS_RESOURCE' || rt.toLowerCase().includes('process')) {
                  return 'process';
                }
                if (rt === 'SYSTEM' || rt.toLowerCase().includes('system')) {
                  return 'system';
                }
                if (rt === 'USER' || rt.toLowerCase().includes('user')) {
                  return 'user';
                }
                return 'user'; // Default fallback
              })(),
              resource_skill: changeType.resource.required_skills.length > 0 ? changeType.resource.required_skills[0] : null,
              description: changeType.resource.description
            } : {}
          };
          
          // Validate required fields before adding
          if (!transitionChange.from_state || typeof transitionChange.from_state !== 'string' || transitionChange.from_state.length === 0) {
            console.warn(`[globalPropertyUtils] Skipping transition change: invalid from_state. Change type:`, changeTypeName, 'State:', state, 'Transition:', transitionStr, 'from_state:', transitionChange.from_state);
            return; // Skip this transition change
          }
          
          if (!transitionChange.to_state || typeof transitionChange.to_state !== 'string' || transitionChange.to_state.length === 0) {
            console.warn(`[globalPropertyUtils] Skipping transition change: invalid to_state. Change type:`, changeTypeName, 'State:', state, 'Transition:', transitionStr, 'to_state:', transitionChange.to_state);
            return; // Skip this transition change
          }
          
          // Skip transition changes that don't have a valid change_type
          if (!transitionChange.change_type) {
            console.warn(`[globalPropertyUtils] Skipping transition change without change_type. Change type:`, changeTypeName, 'State:', state, 'Transition:', transitionStr);
            return; // Skip this transition change
          }
          
          // Validate change_type matches expected values: "System", "User", "Calculate", "Reference" (capitalized)
          // Backend only accepts capitalized values, so we normalize here
          const validChangeTypes: Array<'System' | 'User' | 'Calculate' | 'Reference'> = ['System', 'User', 'Calculate', 'Reference'];
          if (transitionChange.change_type) {
            // Normalize legacy lowercase values to capitalized
            const normalized = transitionChange.change_type.trim();
            const mapping: Record<string, 'System' | 'User' | 'Calculate' | 'Reference'> = {
              'user_input': 'User',
              'calculate': 'Calculate',
              'set': 'System',
              'update': 'System',
              'clear': 'System',
              'copy_from_improvement': 'Reference',
              'User': 'User',
              'Calculate': 'Calculate',
              'System': 'System',
              'Reference': 'Reference'
            };
            const normalizedChangeType = mapping[normalized] || (normalized.charAt(0).toUpperCase() + normalized.slice(1)) as 'System' | 'User' | 'Calculate' | 'Reference';
            
            // Validate it's now a valid capitalized type
            if (validChangeTypes.includes(normalizedChangeType)) {
              transitionChange.change_type = normalizedChangeType;
            } else {
              console.warn(`[globalPropertyUtils] Invalid change_type "${normalized}" (normalized to "${normalizedChangeType}"). Valid types: ${validChangeTypes.join(', ')}. Skipping transition change.`);
              return; // Skip this transition change
            }
          }
          
          // CRITICAL: Backend requires type-specific objects to be present when change_type is set
          // Ensure user_input is present for User change types (backend validation requirement)
          if (transitionChange.change_type === 'User' && !transitionChange.user_input) {
            console.warn(`[globalPropertyUtils] User change_type requires user_input object. Adding empty user_input object. Change type:`, changeTypeName);
            transitionChange.user_input = {
              input_fields: [] // Backend requires this field, even if empty
            };
          }
          
          // Ensure calculations is present for Calculate change types
          if (transitionChange.change_type === 'Calculate' && !transitionChange.calculations) {
            console.warn(`[globalPropertyUtils] Calculate change_type requires calculations object. Skipping transition change. Change type:`, changeTypeName);
            return; // Skip - we can't create a valid Calculate without calculations
          }
          
          // Ensure system is present for System change types
          if (transitionChange.change_type === 'System' && !transitionChange.system) {
            console.warn(`[globalPropertyUtils] System change_type requires system object. Adding empty system object. Change type:`, changeTypeName);
            transitionChange.system = {}; // Backend may require this, even if empty
          }
          
          // Ensure reference is present for Reference change types
          if (transitionChange.change_type === 'Reference' && !transitionChange.reference) {
            console.warn(`[globalPropertyUtils] Reference change_type requires reference object. Adding empty reference object. Change type:`, changeTypeName);
            transitionChange.reference = {
              reference_type: 'simple' // At minimum, we need reference_type
            };
          }
          
          console.log(`[globalPropertyUtils] Created transition change:`, transitionChange);
          transitionChanges.push(transitionChange);
        });
      });
    });
  });
  
  console.log('[globalPropertyUtils] convertChangeTypesToTransitionChanges: Conversion complete', {
    totalTransitionChanges: transitionChanges.length
  });
  
  return transitionChanges;
};
